import uniq from 'lodash/uniq';

import { removeFalsy } from 'Utils/common.utils';
import { getQueryStringFromFilter } from 'Internals/filtering';
import {
  getDownstreamLookups,
  getDownstreamPipes,
  getSinkSystemId,
  getSourceSystemId,
  getUpstreamLookups,
  getUpstreamPipes,
} from 'Internals/pipes';
import { emptyFilterPipes, filterLabelMapping } from 'Constants/pipesListTableDefaults';

import type { DownstreamMap, LookupMap } from 'Types/common.types';
import type {
  LineageDirection,
  LineageFacetKey,
  LineageFilterPipe,
  Pipe,
  PipeMap,
  UpstreamMap,
} from 'Types/pipes.types';

export const getDirectUpstreamPipeOrSystemIds = (args: {
  pipe: Pipe;
  pipesById: PipeMap;
  upstreams: UpstreamMap;
}): string[] => {
  const { pipe, pipesById, upstreams } = args;

  const upstreamPipeIds = getUpstreamPipes(pipe, pipesById, upstreams).map((p) => p._id);
  const sourceSystemId = getSourceSystemId(pipe);

  return uniq(removeFalsy([...upstreamPipeIds, sourceSystemId]));
};

export const getDirectDownstreamPipeOrSystemIds = (args: {
  pipe: Pipe;
  pipesById: PipeMap;
  downstreams: DownstreamMap;
}): string[] => {
  const { pipe, pipesById, downstreams } = args;

  const downstreamPipeIds = getDownstreamPipes(pipe, pipesById, downstreams).map((p) => p._id);
  const sinkSystemId = getSinkSystemId(pipe);

  return uniq(removeFalsy([...downstreamPipeIds, sinkSystemId]));
};

export const getLineageFacetValues = (args: {
  pipes: Pipe[];
  pipesById: PipeMap;
  upstreams: UpstreamMap;
  downstreams: DownstreamMap;
  lookups: LookupMap;
}): LineageFilterPipe[] => {
  const { pipes, pipesById, upstreams, downstreams, lookups } = args;

  return pipes.map((pipe) => {
    const directUpstreamPipeOrSystem = getDirectUpstreamPipeOrSystemIds({
      pipe,
      pipesById,
      upstreams,
    });
    const indirectUpstreamPipeIds = getUpstreamLookups(pipe, pipesById, upstreams).map(
      (lineagePipe) => lineagePipe._id
    );

    const directDownstreamPipeOrSystem = getDirectDownstreamPipeOrSystemIds({
      pipe,
      pipesById,
      downstreams,
    });
    const indirectDownstreamPipeIds = getDownstreamLookups(pipe, pipesById, lookups).map(
      (lineagePipe) => lineagePipe._id
    );

    const upstreamPipeOrSystem = uniq(
      removeFalsy([...directUpstreamPipeOrSystem, ...indirectUpstreamPipeIds])
    );

    const downstreamPipeOrSystem = uniq(
      removeFalsy([...directDownstreamPipeOrSystem, ...indirectDownstreamPipeIds])
    );

    return {
      ...pipe,
      lineage: {
        upstreamPipeOrSystem,
        downstreamPipeOrSystem,
      },
    };
  });
};

export const getLineageRelatedPipeCounts = (args: {
  pipeId: string;
  pipes: Pipe[];
  pipesById: PipeMap;
  upstreams: UpstreamMap;
  downstreams: DownstreamMap;
  lookups: LookupMap;
}): { upstreamCount: number; downstreamCount: number } => {
  const { pipeId, pipes, pipesById, upstreams, downstreams, lookups } = args;

  if (!pipeId) {
    return {
      upstreamCount: 0,
      downstreamCount: 0,
    };
  }

  const pipesWithLineageFacets = getLineageFacetValues({
    pipes,
    pipesById,
    upstreams,
    downstreams,
    lookups,
  });

  const upstreamCount = pipesWithLineageFacets.filter((pipe) =>
    pipe.lineage?.downstreamPipeOrSystem?.includes(pipeId)
  ).length;

  const downstreamCount = pipesWithLineageFacets.filter((pipe) =>
    pipe.lineage?.upstreamPipeOrSystem?.includes(pipeId)
  ).length;

  return {
    upstreamCount,
    downstreamCount,
  };
};

type LineageDirectionBreakdown = {
  totalCount: number;
  directCount: number;
  hopCount: number;
};

export const getLineageRelatedPipeBreakdown = (args: {
  pipeId: string;
  pipes: Pipe[];
  pipesById: PipeMap;
  upstreams: UpstreamMap;
  downstreams: DownstreamMap;
  lookups: LookupMap;
}): {
  upstream: LineageDirectionBreakdown;
  downstream: LineageDirectionBreakdown;
} => {
  const { pipeId, pipes, pipesById, upstreams, downstreams, lookups } = args;

  if (!pipeId) {
    return {
      upstream: {
        totalCount: 0,
        directCount: 0,
        hopCount: 0,
      },
      downstream: {
        totalCount: 0,
        directCount: 0,
        hopCount: 0,
      },
    };
  }

  let upstreamDirectCount = 0;
  let upstreamHopCount = 0;
  let downstreamDirectCount = 0;
  let downstreamHopCount = 0;

  pipes.forEach((pipe) => {
    const directDownstreamPipeIds = getDirectDownstreamPipeOrSystemIds({
      pipe,
      pipesById,
      downstreams,
    });
    const hopDownstreamPipeIds = getDownstreamLookups(pipe, pipesById, lookups).map(
      (lineagePipe) => lineagePipe._id
    );

    if (directDownstreamPipeIds.includes(pipeId)) {
      upstreamDirectCount = upstreamDirectCount + 1;
    } else if (hopDownstreamPipeIds.includes(pipeId)) {
      upstreamHopCount = upstreamHopCount + 1;
    }

    const directUpstreamPipeIds = getDirectUpstreamPipeOrSystemIds({
      pipe,
      pipesById,
      upstreams,
    });
    const hopUpstreamPipeIds = getUpstreamLookups(pipe, pipesById, upstreams).map(
      (lineagePipe) => lineagePipe._id
    );

    if (directUpstreamPipeIds.includes(pipeId)) {
      downstreamDirectCount = downstreamDirectCount + 1;
    } else if (hopUpstreamPipeIds.includes(pipeId)) {
      downstreamHopCount = downstreamHopCount + 1;
    }
  });

  return {
    upstream: {
      totalCount: upstreamDirectCount + upstreamHopCount,
      directCount: upstreamDirectCount,
      hopCount: upstreamHopCount,
    },
    downstream: {
      totalCount: downstreamDirectCount + downstreamHopCount,
      directCount: downstreamDirectCount,
      hopCount: downstreamHopCount,
    },
  };
};

export const getRecursiveLineagePipeIds = (args: {
  startPipeId: string;
  direction: LineageDirection;
  pipesById: PipeMap;
  upstreams: UpstreamMap;
  downstreams: DownstreamMap;
}): string[] => {
  const { startPipeId, direction, pipesById, upstreams, downstreams } = args;

  const startPipe = pipesById[startPipeId];

  if (!startPipe) {
    return [];
  }

  const getNeighbors = (pipe: Pipe): Pipe[] =>
    direction === 'upstream'
      ? getUpstreamPipes(pipe, pipesById, upstreams)
      : getDownstreamPipes(pipe, pipesById, downstreams);

  const collectReachablePipeIds = (queue: Pipe[], visited: Set<string>): string[] => {
    if (queue.length === 0) return [];

    const [current, ...rest] = queue;
    const unvisited = getNeighbors(current).filter((p) => !visited.has(p._id));
    const nextVisited = new Set([...visited, ...unvisited.map((p) => p._id)]);

    return [
      ...unvisited.map((p) => p._id),
      ...collectReachablePipeIds([...rest, ...unvisited], nextVisited),
    ];
  };

  return collectReachablePipeIds([startPipe], new Set([startPipeId]));
};

export const getPipesListUrl = (subId: string, query = '') =>
  `/subscription/${subId}/pipes/all${query}`;

export const getPipesListUrlWithPipeSelection = (subId: string, pipeIds: string[]): string => {
  const selectedPipeIds = uniq(removeFalsy(pipeIds));

  if (selectedPipeIds.length === 0) {
    return getPipesListUrl(subId);
  }

  const filter = {
    ...emptyFilterPipes,
    _id: {
      selected: selectedPipeIds,
      search: [],
    },
  };

  const query = getQueryStringFromFilter(filter, filterLabelMapping);

  return getPipesListUrl(subId, query);
};

export const getPipesListUrlWithLineageSelection = (
  subId: string,
  facetKey: LineageFacetKey,
  selectedValues: string[]
): string => {
  const selected = uniq(removeFalsy(selectedValues));

  if (selected.length === 0) {
    return getPipesListUrl(subId);
  }

  const filter = {
    ...emptyFilterPipes,
    [facetKey]: {
      selected,
      search: [],
    },
  };

  const query = getQueryStringFromFilter(filter, filterLabelMapping);

  return getPipesListUrl(subId, query);
};
